import { useRef, useEffect, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import styled from 'styled-components'
import mapboxgl from 'mapbox-gl'
import useLocation from '../hooks/useGeolocation'
import { useAuth } from '../lib/auth'
import Container from './layout/Container'
import BlueSpaceMapPopup from './BlueSpaceMapPopup'
import VideoMapPopup from './VideoMapPopup'
import AddYourBlueSpaceButton from './AddYourBlueSpaceButton'
import 'mapbox-gl/dist/mapbox-gl.css'

function Map( { 
  blueSpaces,
  center = null,
  zoom = null,
  withButton = true,
  height = '80vh',
  target = '_self',
  interfaceColor = '#0D4466',
} ) {
  const missionState = useRef( 'all' )
  const mapNodeRef = useRef()
  const mapboxMapRef = useRef( null )
  const mapboxBlueSpaceMarkers = useRef( [] )
  const mapboxVideoMarkers = useRef( [] )
  const me = useAuth()
  const mySchoolId = me?.user?.schoolId
  const browserLocation = useLocation()

  const goodBlueHumans = [
    {
      title: 'Stephen Kelly - Marine Conservation Society',
      mission: '1',
      lat: 50.797010,
      lng: -0.431523,
      embed: 'https://youtube.com/embed/FMp_C7VeXAI?feature=share',
    }, {
      title: 'Marina Grissin - Common Seas',
      mission: '1',
      lat: -7.604554,
      lng: 112.108696,
      embed: 'https://youtube.com/embed/mEqT00GkSRY?feature=share',
    }, {
      title: 'Nayantara Jain - Reef Watch India',
      mission: '2',
      lat: 11.867398,
      lng: 92.684017,
      embed: 'https://youtube.com/embed/vg5nBayfzT4?feature=share',
    }, {
      title: 'Rosalin Kuiper - Ocean Race',
      mission: '2',
      lat: 54.353335,
      lng: 10.141404,
      embed: 'https://youtube.com/embed/fbM8WLM9KYQ?feature=share',
    }, {
      title: 'Annukka Pekkarinen - Snowchange Cooperative',
      mission: '2',
      lat: 62.688133,
      lng: 29.998951,
      embed: 'https://youtube.com/embed/lAE38mydyAE?feature=share',
    }, {
      title: 'John Luft - Great Salt Lake Ecosystem Program',
      mission: '2',
      lat: 41.122264,
      lng: -112.494319,
      embed: 'https://youtube.com/embed/qG0YyUnm_m4?feature=share',
    }, {
      title: 'Chris Kastern - Abalobi',
      mission: '3',
      lat: -34.103596,
      lng: 18.484591,
      embed: 'https://youtube.com/embed/lEqvcuUlSUU?feature=share',
    }, {
      title: 'Alan James - Stoney Creek Environmental Committee',
      mission: '3',
      lat: 49.256748,
      lng: -122.903859,
      embed: 'https://youtube.com/embed/pGe4mS_2LOk?feature=share',
    }, {
      title: 'Pam Haigh - Ripple Africa',
      mission: '3',
      lat: -11.737794,
      lng: 34.542904,
      embed: 'https://youtube.com/embed/5WrpXs0L5MY?feature=share',
    }, {
      title: 'Konstantina Kostoula - Common Seas',
      mission: '4',
      lat: 37.086217,
      lng: 25.134501,
      embed: 'https://youtube.com/embed/lBBgdQZ-Yog?feature=share',
    }, {
      title: 'Heather Bell - The Rivers Trust',
      mission: '4',
      lat: 50.544197,
      lng: -4.260579,
      embed: 'https://youtube.com/embed/fYp_-d9MSzY?feature=share',
    }, {
      title: 'Myo Htet Aung -  Worldview International Foundation',
      mission: '5',
      lat: 17.076019,
      lng: 94.465743,
      embed: 'https://youtube.com/embed/Qftg0SfWpH0?feature=share',
    }, {
      title: 'Georgia Wallis',
      mission: '5',
      lat: 50.366404,
      lng: -4.131191,
      embed: 'https://youtube.com/embed/TfrLb_28sgg?feature=share',
    }, {
      title: 'Stacey Alvarez de la Campa - Common Seas',
      mission: '6',
      lat: 13.134406,
      lng: -59.637796,
      embed: 'https://youtube.com/embed/J3Z2iAZEpaI?feature=share',
    }, {
      title: 'Mark Ssemakula, Kavango Zambezi TFCA',
      mission: '6',
      lat: -17.7995,
      lng: 25.1703,
      embed: 'https://www.youtube.com/embed/rVUqLat293s',
    }, {
      title: 'Caroline Höschle & Julika Voß - Space Whale',
      mission: '7',
      lat: 54.489924,
      lng: 9.037574,
      embed: 'https://youtube.com/embed/ZfxB1TmClzo?feature=share',
    }, {
      title: 'Merijn Tinga - Plastic Soup Surfer',
      mission: '7',
      lat: 43.693459,
      lng: 7.277705,
      embed: 'https://youtube.com/embed/0tDdHR7tLV4?feature=share',
    }, {
      title: 'Wim Pyl - Mangata Adventure',
      mission: '7',
      lat: 50.770495,
      lng: 5.456022,
      embed: 'https://youtube.com/embed/U2F4Ar1kZsk?feature=share',
    }, {
      title: 'Dinarte Vieira - MARE Madeira',
      mission: '8',
      lat: 32.630519,
      lng: -16.917468,
      embed: 'https://youtube.com/embed/surfUC9CNnw?feature=share',
    }, {
      title: 'Troy Tuckey - Nepal Water Initiative',
      mission: '8',
      lat: 28.505426,
      lng: 83.658447,
      embed: 'https://youtube.com/embed/my1YTak-tpg?feature=share',
    }, {
      title: 'Brigid Meyers - Earth Echo',
      mission: '8',
      lat: 42.788334,
      lng: -73.697924,
      embed: 'https://youtube.com/embed/iMuUkFn-e8k?feature=share',
    }, {
      title: 'Lea d’Auriol - Oceanic Global',
      mission: '9',
      lat: 40.701659,
      lng: -74.020941,
      embed: 'https://youtube.com/embed/F9wwhzWcH_I?feature=share',
    }, {
      title: 'Lorna Doogan - Mountains to Sea',
      mission: '9',
      lat: -36.779541,
      lng: 174.769523,
      embed: 'https://youtube.com/embed/-DVlYtSollk?feature=share',
    }, {
      title: 'Meg Avon - We are Avon',
      mission: '10',
      lat: 51.440237,
      lng: -2.527125,
      embed: 'https://youtube.com/embed/j7g2fSh5TyI?feature=share',
    }, {
      title: 'Ryann Jibson - Earth Echo',
      mission: '10',
      lat: 45.697954,
      lng: -83.897011,
      embed: 'https://youtube.com/embed/55aEKKBqH0k?feature=share',
    }, {
      title: 'Luke Lerotholi - Youth4MPAS',
      mission: '10',
      lat: -27.635338,
      lng: 32.591424,
      embed: 'https://www.youtube.com/embed/JmXNo_fBBgk?feature=share',
    },
  ]

  const mapCoords = useMemo(
    () => {
      if ( center ) {
        return {
          center,
          zoom: zoom || 8,
        }
      }
      
      if ( browserLocation.done ) {
        return browserLocation.error 
          ? {
            center: [-4, 55],
            zoom: 5,
          } 
          : {
            center: [browserLocation.lng, browserLocation.lat],
            zoom: zoom || 8,
          }
      }
      
      return null
    },
    [
      browserLocation.done, 
      browserLocation.error, 
      browserLocation.lat, 
      browserLocation.lng, 
      center,
      zoom,
    ], 
  )

  const shouldShowButton = (
    withButton 
    && me?.user?.role !== 'ADMIN'
    && !me?.user?.schoolId
  )

  const updateMapboxMission = e => {
    missionState.current = e
    mapboxVideoMarkers.current.forEach( mapboxMarker => {
      if ( mapboxMarker.missionNumber === missionState.current || missionState.current === 'all' ) {
        mapboxMarker.addTo( mapboxMapRef.current ) // Show marker
        // mapboxMarker.getElement().style.opacity = '1';
      }
      else {
        mapboxMarker.remove() // Hide marker
        // mapboxMarker.getElement().style.opacity = '0';
      }
    } )
  }

  const updateMapboxMap = e => {
    if ( e === 'showVideos' ) {
      mapboxBlueSpaceMarkers.current.forEach( mapboxMarker => {
        mapboxMarker.remove() // Hide marker
      } )

      mapboxVideoMarkers.current.forEach( mapboxMarker => {
        // mapboxMarker.addTo( mapboxMapRef.current ) // Show marker
        if ( mapboxMarker.missionNumber === missionState.current || missionState.current === 'all' ) {
          mapboxMarker.addTo( mapboxMapRef.current ) // Show marker
        // mapboxMarker.getElement().style.opacity = '1';
        }
        else {
          mapboxMarker.remove() // Hide marker
        // mapboxMarker.getElement().style.opacity = '0';
        }
      } )
    }

    if ( e === 'showBlueSpaces' ) {
      mapboxVideoMarkers.current.forEach( mapboxMarker => {
        mapboxMarker.remove() // Hide marker
      } )

      mapboxBlueSpaceMarkers.current.forEach( mapboxMarker => {
        mapboxMarker.addTo( mapboxMapRef.current ) // Show marker
      } )
    }

    if ( e === 'showAll' ) {
      mapboxVideoMarkers.current.forEach( mapboxMarker => {
        // mapboxMarker.addTo( mapboxMapRef.current ) // Show marker
        if ( mapboxMarker.missionNumber === missionState.current || missionState.current === 'all' ) {
          mapboxMarker.addTo( mapboxMapRef.current ) // Show marker
        // mapboxMarker.getElement().style.opacity = '1';
        }
        else {
          mapboxMarker.remove() // Hide marker
        // mapboxMarker.getElement().style.opacity = '0';
        }
      } )

      mapboxBlueSpaceMarkers.current.forEach( mapboxMarker => {
        mapboxMarker.addTo( mapboxMapRef.current )
      } )
    }
  }

  useEffect( () => {
    if ( 
      window === undefined 
      || !mapNodeRef.current 
      || !mapCoords
      || mapboxMapRef.current
    ) {
      return
    }
    
    mapboxMapRef.current = new mapboxgl.Map( {
      container: mapNodeRef.current,
      accessToken: process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: mapCoords.center,
      zoom: 2,
      projection: 'mercator',
    } )

    mapboxMapRef.current.on( 'style.load', () => {
      mapboxMapRef.current.getStyle().layers.forEach( layer => {
        // Hide road layers
        if ( layer.id.includes( 'road' ) || layer.id.includes( 'bridge' ) || layer.id.includes( 'tunnel' ) || layer.id.includes( 'transit' ) || layer.id.includes( 'rail' ) ) {
          mapboxMapRef.current.setLayoutProperty( layer.id, 'visibility', 'none' )
        }
      } )
    } )

    if ( mapboxMapRef.current ) {
      mapboxMapRef.current.addControl( new mapboxgl.NavigationControl() )
    }
  }, [mapCoords] )

  useEffect( () => {
    if ( mapCoords && blueSpaces.length && mapboxMapRef.current ) {
      blueSpaces.forEach( blueSpace => {
        const container = document.createElement( 'div' )
        const root = createRoot( container )

        root.render(
          <BlueSpaceMapPopup 
            blueSpace={ blueSpace } 
            mySchoolId={ mySchoolId }
            target={ target }
          />, 
        )

        const popup = new mapboxgl.Popup( {
          anchor: 'bottom',
          closeButton: false,
        } )
          .setDOMContent( container )
          .addTo( mapboxMapRef.current )

        popup.on( 'open', () => {
          mapboxMapRef.current.flyTo( { center: [blueSpace.lng, blueSpace.lat] } )
        } )

        mapboxBlueSpaceMarkers.current.push(
          new mapboxgl.Marker()
            .setLngLat( [blueSpace.lng, blueSpace.lat] )
            .addTo( mapboxMapRef.current )
            .setPopup( popup )
            .togglePopup(),
        )        
      } )

      goodBlueHumans.forEach( goodBlueHuman => {
        const container = document.createElement( 'div' )
        const root = createRoot( container )

        root.render(
          <VideoMapPopup
            iframeTitle={ goodBlueHuman.title }
            embed={ goodBlueHuman.embed }
          />, 
        )

        const popup = new mapboxgl.Popup( {
          anchor: 'bottom',
          closeButton: false,
        } )
          .setDOMContent( container )

        popup.on( 'open', () => {
          mapboxMapRef.current.flyTo( {
            center: [goodBlueHuman.lng, goodBlueHuman.lat],
            offset: [0, 200],
          } )
        } )

        const mapboxVideoMarker = new mapboxgl.Marker( { color: '#F9B233' } )
          .setLngLat( [goodBlueHuman.lng, goodBlueHuman.lat] )
         
        mapboxVideoMarker.missionNumber = goodBlueHuman.mission
        mapboxVideoMarkers.current.push( mapboxVideoMarker )
      } )
    }
  }, [blueSpaces, mySchoolId, mapCoords] )

  return (
    <>
      <StyledMapUI>
        <div>
          <label style={ { color: interfaceColor } } htmlFor="showAll">
            <input onChange={ e => updateMapboxMap( e.target.value ) } type="radio" name="markerRadio" id="showAll" value="showAll" defaultChecked />
            Show Everything
          </label>
          <label style={ { color: interfaceColor } } htmlFor="showBlueSpaces">
            <input onChange={ e => updateMapboxMap( e.target.value ) } type="radio" name="markerRadio" id="showBlueSpaces" value="showBlueSpaces" />
            Blue Spaces
          </label>
          <label style={ { color: interfaceColor } } htmlFor="showVideos">
            <input onChange={ e => updateMapboxMap( e.target.value ) } type="radio" name="markerRadio" id="showVideos" value="showVideos" />
            Good Blue Humans
          </label>
          <select name="selectedMission" onChange={ e => updateMapboxMission( e.target.value ) }>
            <option value="all">All Challenges</option>
            <option value="1">Challenge 1</option>
            <option value="2">Challenge 2</option>
            <option value="3">Challenge 3</option>
            <option value="4">Challenge 4</option>
            <option value="5">Challenge 5</option>
            <option value="6">Challenge 6</option>
            <option value="7">Challenge 7</option>
            <option value="8">Challenge 8</option>
            <option value="9">Challenge 9</option>
            <option value="10">Challenge 10</option>
          </select>
        </div>
      </StyledMapUI>
      <StyledMap $height={ height }>
        <StyledMapContainer ref={ mapNodeRef } />
        <StyledCTAButton>
          <Container>
            { shouldShowButton && <AddYourBlueSpaceButton /> }
          </Container>
        </StyledCTAButton>
      </StyledMap>
    </>
  )
}

const StyledMap = styled.div<{ $height: string }>`
  position: relative;
  height: ${ p => p.$height };
  background: #000000;
  overflow: hidden; 
`

const StyledMapUI = styled.div`
  div {
    text-align: center;
    display: none;
    flex-wrap: wrap;
    justify-content: center;
    margin-bottom: 1em;
  }

  input {
    display: inline-block;
    margin: .5rem 0.25rem 0.5rem 1.75rem;
  }

  select {
    margin-left: 1.5rem;
    padding-left: 0.25rem;
  }

  label {
    cursor:pointer
  }
`

const StyledMapContainer = styled.div`
  width: 100%;
  height: 100%;
`

const StyledCTAButton = styled.div`
  position: absolute;
  width: 100%;
  top: 4rem;
  right: 0;
  pointer-events: none;

  @media ( max-width: 600px ) {
    top: auto;
    right: auto;
    bottom: 4rem;
    left: 0;
  }
`

export default Map
